import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Loader2, Building2 } from 'lucide-react';
import { gmailEntityAssignmentAPI, gmailPdfBillToBackfillAPI } from '../../lib/api';
import { getErrorMessage } from '../../lib/errors';

export function GmailEntityBackfillPanel() {
  const [preview, setPreview] = useState(null);
  const [applyResult, setApplyResult] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [applyLoading, setApplyLoading] = useState(false);

  const [pdfBackfillStatus, setPdfBackfillStatus] = useState(null);
  const [pdfBackfillBusy, setPdfBackfillBusy] = useState(false);
  const [pdfBackfillFailed, setPdfBackfillFailed] = useState(null);

  const runPdfBackfillStart = async () => {
    setPdfBackfillBusy(true);
    setPdfBackfillFailed(null);
    try {
      await gmailPdfBillToBackfillAPI.start();
      const { data } = await gmailPdfBillToBackfillAPI.status();
      setPdfBackfillStatus(data.chain);
    } catch (err) {
      setPdfBackfillFailed(getErrorMessage(err) || 'Failed to start backfill.');
    } finally {
      setPdfBackfillBusy(false);
    }
  };

  const refreshPdfBackfillStatus = async () => {
    setPdfBackfillBusy(true);
    setPdfBackfillFailed(null);
    try {
      const { data } = await gmailPdfBillToBackfillAPI.status();
      setPdfBackfillStatus(data.chain);
    } catch (err) {
      setPdfBackfillFailed(getErrorMessage(err) || 'Failed to fetch status.');
    } finally {
      setPdfBackfillBusy(false);
    }
  };

  const runPreview = async () => {
    setPreviewLoading(true);
    setApplyResult(null);
    try {
      const res = await gmailEntityAssignmentAPI.previewBackfill();
      setPreview(res.data);
    } catch {
      setPreview({ error: 'Failed to run preview.' });
    } finally {
      setPreviewLoading(false);
    }
  };

  const runApply = async () => {
    if (!window.confirm(
      `Resolve entity for ${preview?.would_resolve_count || 0} of ${preview?.candidate_count || 0} unassigned Gmail invoice(s)?`
    )) return;
    setApplyLoading(true);
    try {
      const res = await gmailEntityAssignmentAPI.applyBackfill();
      setApplyResult(res.data);
    } catch {
      setApplyResult({ error: 'Failed to apply backfill.' });
    } finally {
      setApplyLoading(false);
    }
  };

  return (
    <Card>
      <CardHeader className="pb-2">
        <div className="flex items-center gap-2">
          <Building2 className="h-4 w-4 text-muted-foreground" />
          <CardTitle className="text-base">Gmail Entity Resolution Backfill</CardTitle>
        </div>
        <p className="text-xs text-muted-foreground">
          Runs the deterministic resolver (mailbox mapping, doc-text identifier match, counterparty
          mapping) over existing unassigned Gmail invoices. Two-step: Preview, then Apply.
        </p>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={runPreview} disabled={previewLoading} data-testid="gmail-entity-backfill-preview">
            {previewLoading ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : null}
            Preview
          </Button>
          {preview && !preview.error && preview.candidate_count > 0 && (
            <Button
              variant="default"
              size="sm"
              onClick={runApply}
              disabled={applyLoading || !!applyResult}
              data-testid="gmail-entity-backfill-apply"
            >
              {applyLoading ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : null}
              Apply to {preview.would_resolve_count} Invoices
            </Button>
          )}
        </div>

        {preview && (
          <>
            {preview.error ? (
              <p className="text-xs text-red-600">{preview.error}</p>
            ) : (
              <div className="space-y-2" data-testid="gmail-entity-backfill-preview-result">
                <div className="flex flex-wrap gap-3 text-xs">
                  <span>Candidates: <strong>{preview.candidate_count}</strong></span>
                  <span>Would resolve: <strong>{preview.would_resolve_count}</strong></span>
                  <span>
                    Residual unassigned:{' '}
                    <strong className={preview.unresolved_residual > 0 ? 'text-amber-600' : ''}>
                      {preview.unresolved_residual}
                    </strong>
                  </span>
                </div>
                {preview.by_tier && Object.keys(preview.by_tier).length > 0 && (
                  <div className="text-xs">
                    <span className="font-medium">By tier: </span>
                    {Object.entries(preview.by_tier).map(([tier, count]) => (
                      <Badge key={tier} variant="outline" className="text-[10px] mr-1">
                        {tier}: {count}
                      </Badge>
                    ))}
                  </div>
                )}
                <p className="text-xs text-muted-foreground">{preview.message}</p>
              </div>
            )}
          </>
        )}

        {applyResult && (
          <div className={`p-3 rounded text-xs border ${applyResult.error ? 'bg-red-50 border-red-200' : 'bg-green-50 border-green-200'}`} data-testid="gmail-entity-backfill-apply-result">
            {applyResult.error ? (
              <p className="text-red-600">{applyResult.error}</p>
            ) : (
              <div className="space-y-1">
                <p><strong>Stamped:</strong> {applyResult.stamped} of {applyResult.candidate_count}</p>
                <p><strong>Residual unassigned:</strong> {applyResult.unresolved_residual}</p>
              </div>
            )}
          </div>
        )}

        <div className="pt-3 border-t space-y-3">
          <div>
            <p className="text-sm font-medium">pdf_bill_to backfill</p>
            <p className="text-xs text-muted-foreground">
              For the residual left unassigned above: re-runs the resolver with each doc's PDF
              attachment fetched, so a Bill-to block naming exactly one registered entity resolves it
              even when the email itself carries no text hint. Re-fetches from Gmail per doc, so this
              runs as a background chain, not an instant preview -- click Start, then Refresh status to
              watch it progress. A PDF naming zero or 2+ registered entities stays unassigned with the
              reason recorded; a match that contradicts an existing counterparty mapping is flagged
              entity_conflict and never auto-assigned.
            </p>
          </div>

          <div className="flex items-center gap-2 flex-wrap">
            <Button
              variant="outline" size="sm" onClick={runPdfBackfillStart} disabled={pdfBackfillBusy}
              data-testid="gmail-pdf-bill-to-backfill-start"
            >
              {pdfBackfillBusy ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : null}
              Start pdf_bill_to backfill
            </Button>
            {pdfBackfillStatus && (
              <Button
                variant="ghost" size="sm" onClick={refreshPdfBackfillStatus} disabled={pdfBackfillBusy}
                data-testid="gmail-pdf-bill-to-backfill-refresh"
              >
                Refresh status
              </Button>
            )}
          </div>

          {pdfBackfillFailed && (
            <div
              className="p-3 rounded text-xs border bg-red-50 border-red-200 dark:bg-red-950/30 dark:border-red-900"
              data-testid="gmail-pdf-bill-to-backfill-error"
            >
              <p className="text-red-600 dark:text-red-400">{pdfBackfillFailed}</p>
            </div>
          )}

          {pdfBackfillStatus && (
            <div className="space-y-1" data-testid="gmail-pdf-bill-to-backfill-status">
              <p className="text-xs text-muted-foreground">
                Backfill: {pdfBackfillStatus.status}
                {' · '}{pdfBackfillStatus.progress?.cohort_processed ?? 0} of{' '}
                {pdfBackfillStatus.metadata?.cohort_count ?? '?'} processed
              </p>
              {pdfBackfillStatus.progress?.tallies && (
                <div className="flex flex-wrap gap-2">
                  {Object.entries(pdfBackfillStatus.progress.tallies).map(([key, count]) => (
                    count > 0 && (
                      <Badge key={key} variant="outline" className="text-[10px]" data-testid={'gmail-pdf-bill-to-backfill-tally-' + key}>
                        {key}: {count}
                      </Badge>
                    )
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
